const safeDivide = (value, divisor) => divisor > 0 ? value / divisor : 0;
const finite = (value) => Number.isFinite(value) ? value : 0;

export const calculateUnitEconomics = (input) => {
  const impressions = safeDivide(input.marketingSpend, input.cpm) * 1000;
  const visits = impressions * input.ctr;
  const funnelInstalls = visits * input.installConversion;
  const cpiInstalls = safeDivide(input.marketingSpend, input.cpi);
  const installs = Math.min(funnelInstalls, cpiInstalls);
  const signups = installs * input.signupConversion;
  const activatedUsers = signups * input.activationRate;
  const firstActions = activatedUsers * input.firstActionRate;
  const completedHappenings = firstActions * input.completionRate;
  const retainedUsers = activatedUsers * input.d30Retention;
  const payingUsers = retainedUsers * input.paidConversion;
  const gmv = payingUsers * input.arppu * input.transactionFrequency;
  const revenue = gmv * input.takeRate;
  const cac = safeDivide(input.marketingSpend, activatedUsers);
  const costPerSignup = safeDivide(input.marketingSpend, signups);
  const costPerHappening = safeDivide(input.marketingSpend, completedHappenings);
  const monthlyMarginPerActivated = input.paidConversion * input.arppu * input.transactionFrequency * input.takeRate * input.grossMargin;
  const lifetimeMonths = safeDivide(1, 1 - Math.min(.95, input.d90Retention));
  const ltv = monthlyMarginPerActivated * lifetimeMonths;
  return {
    impressions, visits, installs, signups, activatedUsers, firstActions, completedHappenings,
    retainedUsers, payingUsers, gmv, revenue, cac, costPerSignup,
    costPerActivated: cac, costPerHappening, ltv,
    ltvCac: safeDivide(ltv, cac), paybackMonths: safeDivide(cac, monthlyMarginPerActivated),
  };
};

export const calculateRevenue = (input) => {
  const transactingUsers = input.mau * input.transactingRate;
  const monthlyTransactions = transactingUsers * input.transactionsPerUser;
  const monthlyGmv = monthlyTransactions * input.averageValue;
  const platformRevenue = monthlyGmv * input.platformRate;
  const subscribers = input.mau * input.subscriberRate;
  const subscriptionRevenue = subscribers * input.subscriptionArpu;
  const aiCreditRevenue = input.mau * input.aiCreditRevenuePerMau;
  const boostRevenue = input.mau * input.boostRevenuePerMau;
  const monthlyNetRevenue = platformRevenue + subscriptionRevenue + aiCreditRevenue + boostRevenue;
  return {
    transactingUsers, monthlyTransactions, monthlyGmv, platformRevenue, subscribers,
    subscriptionRevenue, aiCreditRevenue, boostRevenue, monthlyNetRevenue,
    annualizedRevenue: monthlyNetRevenue * 12,
    revenuePerMau: safeDivide(monthlyNetRevenue, input.mau),
    grossMarginValue: monthlyNetRevenue * input.grossMargin,
    creatorEarnings: monthlyGmv * input.creatorRate * input.creatorShare,
    creditCirculation: input.mau * input.creditCirculationPerMau,
  };
};

const retainedCohort = (size, age, d30, d90) => {
  if (age === 0) return size;
  if (age === 1) return size * d30;
  if (age === 2) return size * Math.sqrt(d30 * d90);
  return size * d90 * Math.pow(.97, age - 3);
};

export const buildTwentyFourMonthModel = ({ fundraise, funds, unit, revenue, cityCount }) => {
  const marketingBudget = funds.find((item) => item.key === "acquisition")?.amount_inr || 0;
  const nonMarketingBudget = Math.max(0, fundraise.raise_inr - marketingBudget);
  const ramp = [0.45,.5,.55,.6,.68,.75,.82,.9,.96,1,1.05,1.1,1.12,1.15,1.18,1.2,1.22,1.24,1.25,1.27,1.28,1.3,1.32,1.35];
  const rampTotal = ramp.reduce((sum, value) => sum + value, 0);
  const cohorts = [];
  let cash = fundraise.raise_inr;
  return ramp.map((weight, monthIndex) => {
    const marketingSpend = marketingBudget * weight / rampTotal;
    const paidInstalls = safeDivide(marketingSpend, unit.cpi);
    const activated = paidInstalls * unit.signupConversion * unit.activationRate;
    const acquiredWithReferral = activated * (1 + unit.referralRate);
    cohorts.push(acquiredWithReferral);
    const mau = cohorts.reduce((sum, cohort, cohortIndex) => sum + retainedCohort(cohort, monthIndex - cohortIndex, unit.d30Retention, unit.d90Retention), 0);
    const completedHappenings = mau * unit.firstActionRate * unit.completionRate;
    const revenueOutput = calculateRevenue({ ...revenue, mau });
    const operatingBurn = nonMarketingBudget / 24;
    const netBurn = operatingBurn + marketingSpend - revenueOutput.monthlyNetRevenue;
    cash -= netBurn;
    return {
      month: monthIndex + 1,
      marketingSpend,
      activatedUsers: acquiredWithReferral,
      mau: finite(mau),
      completedHappenings: finite(completedHappenings),
      netRevenue: finite(revenueOutput.monthlyNetRevenue),
      cash: finite(cash),
      cities: Math.min(cityCount, 2 + Math.floor(monthIndex / Math.max(1, 24 / Math.max(1, cityCount - 1)))),
    };
  });
};

export const formatInr = (value, compact = true) => {
  const amount = finite(value);
  if (compact && Math.abs(amount) >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (compact && Math.abs(amount) >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);
};

export const formatNumber = (value) => new Intl.NumberFormat("en-IN", { notation: Math.abs(value) >= 10000 ? "compact" : "standard", maximumFractionDigits: 1 }).format(finite(value));